(() => {
  'use strict';
  if (customElements.get('apex-devil')) return;
  const WIDTH = 1122, HEIGHT = 1402;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  // These masks follow the existing illustration. The head, clothes, arms,
  // hands and legs are never separated or transformed.
  // The original root and trouser seam stay intact. Include a transparent
  // margin around the free tail so its entire black outline moves with it.
  const tailPath = new Path2D('M780 790H920V1015H765L758 1007V929H780Z');
  const leftEye = new Path2D('M450 367L451 367L464 374L519 405L520 406L519 410L518 412.5L516 416L513 420L510 423L507 425L505 426L501 427L487 427L480 424L475 421L467 413L464 409L461 404L456 393.5L452 381L451 376.5L450 369Z');
  const rightEye = new Path2D('M598 387L605 380L613 373L627 361L633 356L659 335L663 332L674 324L683 318L691 313L695 311L696 311L697 315L698 321L698 340L697 349L695 357.5L693 364L688 374L686 377L681 383L676 388L672 391L667 394L661 397L655 399L651 400L646 401L639 402L626 402L620 401L613 399L610 398L608 397L602 392Z');

  class ApexDevil extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abort = new AbortController();
      this.poster = this.querySelector('img');
      this.canvas = this.querySelector('canvas');
      this.pauseButton = this.querySelector('[data-devil-pause]');
      this.ctx = this.canvas?.getContext('2d', { alpha: true });
      if (!this.ctx || !this.poster) return;
      this.ready = false; this.loading = false; this.paused = false;
      this.frames = 0; this.clock = 0; this.frameId = null;
      this.nextBlink = 2200; this.blinkStart = -Infinity;
      this.gaze = { x: 0, y: 0 }; this.target = { x: 0, y: 0 };
      this.visible = false; this.rect = null;
      this.removeAttribute('data-ready'); this.removeAttribute('data-paused');
      this.removeAttribute('data-reduced'); this.removeAttribute('data-fallback');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.coarse = matchMedia('(pointer: coarse)');
      this.onFrame = this.onFrame.bind(this);
      const signal = this.abort.signal;
      document.addEventListener('pointermove', event => {
        if (!this.ready || !this.visible || this.paused || this.reduced.matches || event.pointerType === 'touch') return;
        const bounds = this.rect || (this.rect = this.canvas.getBoundingClientRect());
        this.target.x = clamp((event.clientX - bounds.left - bounds.width * 0.51) / Math.max(1, bounds.width * 0.7), -1, 1);
        this.target.y = clamp((event.clientY - bounds.top - bounds.height * 0.26) / Math.max(1, bounds.height * 0.7), -1, 1);
      }, { passive: true, signal });
      document.documentElement.addEventListener('pointerleave', () => { this.target.x = 0; this.target.y = 0; }, { signal });
      window.addEventListener('scroll', () => { this.rect = null; }, { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
      this.pauseButton?.addEventListener('click', () => this.setPaused(!this.paused), { signal });
      this.reduced.addEventListener('change', () => {
        this.toggleAttribute('data-reduced', this.reduced.matches);
        if (!this.reduced.matches && !this.ready) this.scheduleLoad();
        this.syncPlayback();
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas);
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.rect = null; this.syncPlayback();
      }, { threshold: 0.01 });
      this.intersectionObserver.observe(this);
      if (this.reduced.matches || this.dataset.motion === 'false') { this.setAttribute('data-reduced', ''); return; }
      if (document.readyState === 'complete') this.scheduleLoad();
      else window.addEventListener('load', () => this.scheduleLoad(), { once: true, signal });
    }

    disconnectedCallback() {
      this.connected = false; this.abort?.abort();
      this.resizeObserver?.disconnect(); this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.frameId); this.frameId = null;
      if (this.idleId && window.cancelIdleCallback) cancelIdleCallback(this.idleId);
      clearTimeout(this.loadTimer);
    }

    scheduleLoad() {
      if (!this.connected || this.loading || this.ready || this.reduced.matches || this.dataset.motion === 'false' || this.dataset.original !== 'true') return;
      this.loading = true;
      if ('requestIdleCallback' in window) this.idleId = requestIdleCallback(() => this.prepare(), { timeout: 1200 });
      else this.loadTimer = setTimeout(() => this.prepare(), 50);
    }

    async prepare() {
      try {
        // Reuse the image already loaded by the page: no new animation image,
        // sprite atlas or animation library is downloaded.
        await this.poster.decode();
        if (!this.connected || this.poster.naturalWidth !== WIDTH || this.poster.naturalHeight !== HEIGHT) return;
        this.body = document.createElement('canvas');
        this.body.width = WIDTH; this.body.height = HEIGHT;
        const body = this.body.getContext('2d');
        body.drawImage(this.poster, 0, 0);
        body.globalCompositeOperation = 'destination-out';
        body.fill(tailPath);
        this.tail = document.createElement('canvas');
        this.tail.width = 220; this.tail.height = 245;
        const tail = this.tail.getContext('2d');
        tail.translate(-700, -790); tail.clip(tailPath); tail.drawImage(this.poster, 0, 0);
        this.ready = true; this.resize(); this.render();
        this.setAttribute('data-ready', ''); this.syncPlayback();
      } catch { this.setAttribute('data-fallback', ''); }
    }

    resize() {
      this.rect = null;
      const bounds = this.canvas.getBoundingClientRect();
      if (!bounds.width) return;
      const dpr = Math.min(devicePixelRatio || 1, this.coarse.matches ? 1.25 : 1.5);
      const scale = Math.min(bounds.width * dpr / WIDTH, Math.sqrt(900000 / (WIDTH * HEIGHT)));
      const width = Math.max(1, Math.round(WIDTH * scale));
      const height = Math.max(1, Math.round(HEIGHT * scale));
      if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; }
      if (this.ready) this.render();
    }

    setPaused(value) {
      this.paused = value; this.toggleAttribute('data-paused', value);
      this.pauseButton?.setAttribute('aria-pressed', String(value));
      this.pauseButton?.setAttribute('aria-label', value ? 'Resume mascot animation' : 'Pause mascot animation');
      this.syncPlayback();
    }

    syncPlayback() {
      const play = this.ready && this.connected && this.visible && !this.paused && !this.reduced.matches && this.dataset.motion !== 'false' && !document.hidden;
      this.toggleAttribute('data-playing', play);
      if (play && !this.frameId) {
        this.lastTick = null; this.lastRender = -Infinity;
        this.frameId = requestAnimationFrame(this.onFrame);
      } else if (!play) {
        cancelAnimationFrame(this.frameId); this.frameId = null; this.lastTick = null;
      }
    }

    onFrame(timestamp) {
      this.frameId = requestAnimationFrame(this.onFrame);
      if (timestamp - this.lastRender < 1000 / 30 - 1) return;
      const elapsed = this.lastTick === null ? 0 : Math.min(80, timestamp - this.lastTick);
      this.lastTick = timestamp; this.lastRender = timestamp; this.clock += elapsed;
      const follow = 1 - Math.exp(-elapsed / 170);
      this.gaze.x += (this.target.x - this.gaze.x) * follow;
      this.gaze.y += (this.target.y - this.gaze.y) * follow;
      if (this.clock > this.nextBlink) {
        this.blinkStart = this.clock; this.nextBlink = this.clock + 3500 + Math.random() * 3500;
      }
      this.render(); this.frames++;
    }

    drawEye(path, cx, cy, pupilX, pupilY, rx, ry, open, skin, range, reflectionX = 2, reflectionFraction = 0.45, reflectionScale = 1) {
      const c = this.ctx;
      c.save(); c.clip(path);
      c.fillStyle = skin; c.fillRect(cx - 65, cy - 65, 130, 130);
      if (open > 0.015) {
        c.translate(cx, cy); c.scale(1, open); c.translate(-cx, -cy);
        const white = c.createLinearGradient(0, cy - 45, 0, cy + 40);
        white.addColorStop(0, '#ffffff'); white.addColorStop(1, '#e4e5e8');
        c.fillStyle = white; c.fill(path);
        c.fillStyle = '#070708'; c.beginPath();
        c.ellipse(pupilX + this.gaze.x * range, pupilY + this.gaze.y * 4, rx, ry, -0.2, 0, Math.PI * 2); c.fill();
        // Align the pair within the visible pupils. Their geometric centres
        // sit partly behind different slanted lids, so a shared centre offset
        // would put one reflection against its lid and the other in open space.
        const px = pupilX + this.gaze.x * range, py = pupilY + this.gaze.y * 4;
        const lightX = px + reflectionX;
        const cos = Math.cos(-0.2), sin = Math.sin(-0.2);
        const a = sin * sin / (rx * rx) + cos * cos / (ry * ry);
        const b = 2 * reflectionX * cos * sin * (1 / (rx * rx) - 1 / (ry * ry));
        const d = reflectionX * reflectionX * (cos * cos / (rx * rx) + sin * sin / (ry * ry)) - 1;
        const root = Math.sqrt(Math.max(0, b * b - 4 * a * d));
        const lid = path === leftEye ? 374 + (lightX - 464) * 31 / 55 : 356 - (lightX - 633) * 21 / 26;
        const top = Math.max(lid, py + (-b - root) / (2 * a));
        const bottom = py + (-b + root) / (2 * a);
        const height = Math.max(0, bottom - top);
        const lightY = top + height * reflectionFraction;
        const size = reflectionScale * Math.min(1, height / 14);
        c.save(); c.clip(); c.fillStyle = '#fafafa'; c.beginPath();
        c.ellipse(lightX, lightY, 1.5 * size, 2.5 * size, 0, 0, Math.PI * 2); c.fill();
        c.restore();
      }
      c.restore();
      // Cover the antialiased mask seam with the existing black eye outline.
      // This keeps each moving pupil joined to its lid without a pale hairline.
      c.save(); c.strokeStyle = '#070708'; c.lineWidth = 2;
      c.lineJoin = 'round'; c.stroke(path); c.restore();
    }

    drawTail(angle) {
      const c = this.ctx;
      if (Math.abs(angle) < 0.00001) { c.drawImage(this.tail, 700, 790); return; }
      const point = (x, y) => {
        const t = clamp((x - 758) / 52, 0, 1);
        const bend = angle * t * t * (3 - 2 * t);
        const cos = Math.cos(bend), sin = Math.sin(bend);
        return { u: x - 700, v: y - 790, x: 732 + (x - 732) * cos - (y - 953) * sin, y: 953 + (x - 732) * sin + (y - 953) * cos };
      };
      const triangle = (p, q, r) => {
        const det = (q.u - p.u) * (r.v - p.v) - (r.u - p.u) * (q.v - p.v);
        const a = ((q.x - p.x) * (r.v - p.v) - (r.x - p.x) * (q.v - p.v)) / det;
        const b = ((q.y - p.y) * (r.v - p.v) - (r.y - p.y) * (q.v - p.v)) / det;
        const d = ((r.y - p.y) * (q.u - p.u) - (q.y - p.y) * (r.u - p.u)) / det;
        const h = ((r.x - p.x) * (q.u - p.u) - (q.x - p.x) * (r.u - p.u)) / det;
        // Overlap along each edge prevents antialiased gaps, including the
        // almost vertical edges of these narrow mesh triangles.
        c.save(); c.beginPath(); c.moveTo(p.x, p.y); c.lineTo(q.x, q.y); c.lineTo(r.x, r.y); c.closePath();
        for (const [start, end] of [[p, q], [q, r], [r, p]]) {
          const dx = end.x - start.x, dy = end.y - start.y, length = Math.hypot(dx, dy);
          const nx = dy / length, ny = -dx / length;
          c.moveTo(start.x + nx, start.y + ny); c.lineTo(end.x + nx, end.y + ny);
          c.lineTo(end.x - nx, end.y - ny); c.lineTo(start.x - nx, start.y - ny); c.closePath();
          c.moveTo(start.x + 1, start.y); c.arc(start.x, start.y, 1, 0, Math.PI * 2); c.closePath();
        }
        c.clip();
        c.transform(a, b, h, d, p.x - a * p.u - h * p.v, p.y - b * p.u - d * p.v);
        c.drawImage(this.tail, 0, 0); c.restore();
      };
      for (let x = 758; x < 810; x += 10) {
        const next = Math.min(x + 10, 810);
        const a = point(x, 790), b = point(next, 790), d = point(x, 1035), e = point(next, 1035);
        triangle(a, b, e); triangle(a, e, d);
      }
      c.save(); c.translate(732, 953); c.rotate(angle);
      c.drawImage(this.tail, 110, 0, 110, 245, 810 - 732, 790 - 953, 110, 245); c.restore();
    }

    render() {
      if (!this.ready || !this.body || !this.tail) return;
      const c = this.ctx;
      c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, this.canvas.width, this.canvas.height);
      c.setTransform(this.canvas.width / WIDTH, 0, 0, this.canvas.height / HEIGHT, 0, 0);
      // Bend the free tail gradually while pinning its full original root.
      this.drawTail(Math.sin(this.clock / 1000 * 1.75) * 0.055);
      c.drawImage(this.body, 0, 0);
      const blinkTime = this.clock - this.blinkStart;
      const blink = blinkTime >= 0 && blinkTime < 190 ? Math.pow(Math.sin(blinkTime / 190 * Math.PI), 0.8) : 0;
      this.drawEye(leftEye, 486, 402, 503, 393, 9, 19, 1 - blink, '#df0b12', 7);
      this.drawEye(rightEye, 649, 373, 646, 348, 10, 24, 1 - blink, '#ed1116', 9);
    }
  }
  customElements.define('apex-devil', ApexDevil);
})();
